import { useCallback, useEffect, useRef, useState } from 'react';
import useCallbackAsync from './useCallbackAsync';
import { AxiosResponse } from 'axios';

type useCacheFetchProps<T> = {
  init?: T;
  resetCache?: any[];
  onReset?: () => void;
  fetchData: () => Promise<AxiosResponse<T>>;
};

export default function useCacheFetch<T>({
  init = undefined,
  resetCache = [],
  onReset,
  fetchData,
}: useCacheFetchProps<T>) {
  const [data, setData] = useState<T>(init);

  const isFetched = useRef(false);

  const getData = useCallback(async () => {
    const { data } = await fetchData();
    setData(data);
    isFetched.current = true;
  }, [fetchData]);

  const [fetch, isLoading] = useCallbackAsync(async (timeout?: number) => {
    if (isFetched.current) return;

    if (timeout)
      await new Promise((resolve, reject) =>
        setTimeout(() => getData().then(resolve).catch(reject), timeout),
      );
    else await getData();
  }, false);

  useEffect(() => {
    if (!isFetched.current) return;

    isFetched.current = false;
    onReset?.();
    fetch(1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...resetCache, init]);

  return { data, isLoading, fetch };
}
